import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth";
import { AppShell } from "./components/AppShell";
import { PageState } from "./components/ui";
import { canUseGovernance } from "./experience";
import { pathWithLocale, useI18n } from "./i18n";
import { AuthPage } from "./pages/AuthPage";

const AgentDetailPage = lazy(() => import("./pages/AgentsPage").then((module) => ({ default: module.AgentDetailPage })));
const AgentListPage = lazy(() => import("./pages/AgentsPage").then((module) => ({ default: module.AgentListPage })));
const AgentRunPage = lazy(() => import("./pages/AgentsPage").then((module) => ({ default: module.AgentRunPage })));
const AdjudicationDetailPage = lazy(() => import("./pages/AdjudicationPage").then((module) => ({ default: module.AdjudicationDetailPage })));
const AdjudicationListPage = lazy(() => import("./pages/AdjudicationPage").then((module) => ({ default: module.AdjudicationListPage })));
const CalibrationPage = lazy(() => import("./pages/CalibrationPage").then((module) => ({ default: module.CalibrationPage })));
const ConnectionsPage = lazy(() => import("./pages/ConnectionsPage").then((module) => ({ default: module.ConnectionsPage })));
const DuplicateDetailPage = lazy(() => import("./pages/DuplicatesPage").then((module) => ({ default: module.DuplicateDetailPage })));
const DuplicateListPage = lazy(() => import("./pages/DuplicatesPage").then((module) => ({ default: module.DuplicateListPage })));
const IngestionPage = lazy(() => import("./pages/IngestionPage").then((module) => ({ default: module.IngestionPage })));
const InvitationPage = lazy(() => import("./pages/InvitationPage").then((module) => ({ default: module.InvitationPage })));
const KnowledgeDetailPage = lazy(() => import("./pages/KnowledgePage").then((module) => ({ default: module.KnowledgeDetailPage })));
const KnowledgeInventoryPage = lazy(() => import("./pages/KnowledgePage").then((module) => ({ default: module.KnowledgeInventoryPage })));
const OverviewPage = lazy(() => import("./pages/OverviewPage").then((module) => ({ default: module.OverviewPage })));
const PlatformPage = lazy(() => import("./pages/PlatformPage").then((module) => ({ default: module.PlatformPage })));
const ReviewDetailPage = lazy(() => import("./pages/ReviewsPage").then((module) => ({ default: module.ReviewDetailPage })));
const ReviewListPage = lazy(() => import("./pages/ReviewsPage").then((module) => ({ default: module.ReviewListPage })));
const SearchPage = lazy(() => import("./pages/SearchPage").then((module) => ({ default: module.SearchPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((module) => ({ default: module.SettingsPage })));
const TrustPage = lazy(() => import("./pages/TrustPage").then((module) => ({ default: module.TrustPage })));
const WorkbenchPage = lazy(() => import("./pages/WorkbenchPage").then((module) => ({ default: module.WorkbenchPage })));
const WorkbenchKnowledgePage = lazy(() => import("./pages/WorkbenchKnowledgePage").then((module) => ({ default: module.WorkbenchKnowledgePage })));

function SuspendedPage({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  return <Suspense fallback={<PageState kind="loading" title={locale === "zh" ? "正在加载工作流" : "Loading workflow"} />}>{children}</Suspense>;
}

function LocaleRedirect({ to }: { to: string }) {
  const { locale } = useI18n();
  return <Navigate replace to={pathWithLocale(to, locale)} />;
}

function RequireAuth() {
  const auth = useAuth();
  const location = useLocation();
  const { locale } = useI18n();
  if (auth.status === "loading") {
    return <div className="fullscreen-state"><PageState kind="loading" title={locale === "zh" ? "正在恢复安全会话" : "Restoring your secure session"} /></div>;
  }
  if (auth.status === "error") {
    return <div className="fullscreen-state"><PageState kind="error" message={auth.bootError ?? undefined} onRetry={() => void auth.refresh()} title={locale === "zh" ? "暂时无法连接 Assay" : "Assay is temporarily unavailable"} /></div>;
  }
  if (auth.status === "anonymous") {
    return <Navigate replace state={{ from: `${location.pathname}${location.search}` }} to={`/login?lang=${locale}`} />;
  }
  return <Outlet />;
}

function RequireGovernance() {
  const auth = useAuth();
  const { locale } = useI18n();
  if (!canUseGovernance(auth.workspace?.role)) {
    return <Navigate replace to={pathWithLocale("/work", locale)} />;
  }
  return <Outlet />;
}

export function App() {
  return (
    <Routes>
      <Route element={<LocaleRedirect to="/app" />} path="/" />
      <Route element={<AuthPage mode="login" />} path="/login" />
      <Route element={<AuthPage mode="register" />} path="/register" />
      <Route element={<SuspendedPage><InvitationPage /></SuspendedPage>} path="/invite/:token" />
      <Route element={<SuspendedPage><InvitationPage /></SuspendedPage>} path="/accept-invitation/:token" />
      <Route element={<RequireAuth />}>
        <Route element={<SuspendedPage><WorkbenchPage /></SuspendedPage>} path="/work" />
        <Route element={<SuspendedPage><WorkbenchPage /></SuspendedPage>} path="/work/answers/:answerId" />
        <Route element={<SuspendedPage><WorkbenchKnowledgePage /></SuspendedPage>} path="/work/knowledge/:objectId" />
        <Route element={<RequireGovernance />}>
        <Route element={<AppShell />} path="/app">
          <Route element={<SuspendedPage><OverviewPage /></SuspendedPage>} index />
          <Route element={<SuspendedPage><ConnectionsPage /></SuspendedPage>} path="connections" />
          <Route element={<SuspendedPage><IngestionPage /></SuspendedPage>} path="ingestion" />
          <Route element={<SuspendedPage><KnowledgeInventoryPage /></SuspendedPage>} path="knowledge" />
          <Route element={<SuspendedPage><KnowledgeDetailPage /></SuspendedPage>} path="knowledge/:objectId" />
          <Route element={<SuspendedPage><DuplicateListPage /></SuspendedPage>} path="duplicates" />
          <Route element={<SuspendedPage><DuplicateDetailPage /></SuspendedPage>} path="duplicates/:clusterId" />
          <Route element={<SuspendedPage><TrustPage /></SuspendedPage>} path="trust" />
          <Route element={<SuspendedPage><ReviewListPage /></SuspendedPage>} path="reviews" />
          <Route element={<SuspendedPage><ReviewDetailPage /></SuspendedPage>} path="reviews/:taskId" />
          <Route element={<SuspendedPage><AdjudicationListPage /></SuspendedPage>} path="adjudication" />
          <Route element={<SuspendedPage><AdjudicationDetailPage /></SuspendedPage>} path="adjudication/:caseId" />
          <Route element={<SuspendedPage><CalibrationPage /></SuspendedPage>} path="calibration" />
          <Route element={<SuspendedPage><SearchPage /></SuspendedPage>} path="search" />
          <Route element={<SuspendedPage><AgentListPage /></SuspendedPage>} path="agents" />
          <Route element={<SuspendedPage><AgentDetailPage /></SuspendedPage>} path="agents/:agentId" />
          <Route element={<SuspendedPage><AgentRunPage /></SuspendedPage>} path="agents/:agentId/runs/:runId" />
          <Route element={<SuspendedPage><PlatformPage /></SuspendedPage>} path="platform" />
          <Route element={<SuspendedPage><SettingsPage /></SuspendedPage>} path="settings" />
        </Route>
        </Route>
      </Route>
      <Route element={<LocaleRedirect to="/app" />} path="*" />
    </Routes>
  );
}
